import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Check, X, Play } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { PageBody } from '@/components/layout/PageBody';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useProject } from '@/contexts/ProjectContext';
import {
  getOutcomeDetail,
  updateOutcomeConvergenceScore,
  updateOutcomeStatus,
  getOutcomeConversation,
  executeOutcome,
} from '@/lib/api';
import { OutcomeChatPanel } from '@/components/outcomes/OutcomeChatPanel';
import { TestLayerCard } from '@/components/outcomes/TestLayerCard';
import { ConvergenceChart } from '@/components/outcomes/ConvergenceChart';
import { ConvergenceBreakdownCard } from '@/components/outcomes/ConvergenceBreakdownCard';
import type { AuditEntry, OutcomeSummary, CodebaseResponse } from '@/lib/api';
import { cn } from '@/lib/utils';

const PHASES = ['Define', 'Decompose', 'Execute', 'Verify', 'Converge', 'Sign-Off'] as const;

function phaseIndex(status: string): number {
  switch (status) {
    case 'draft':
      return 0;
    case 'approved':
      return 1;
    case 'in-progress':
      return 2;
    case 'conditional':
      return 3;
    case 'converging':
      return 4;
    case 'rework':
      return 2;
    case 'complete':
      return 5;
    default:
      return 0;
  }
}

function convergenceColor(score: number | null): string {
  if (score === null || score === undefined) return 'text-text-tertiary';
  if (score >= 90) return 'text-success';
  if (score >= 70) return 'text-warning';
  return 'text-error';
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function OutcomeDetailPage(): React.ReactElement {
  const { outcomeId } = useParams<{ outcomeId: string }>();
  const navigate = useNavigate();
  const { selectedProjectId, setSelectedProjectId, codebases, isLoadingCodebases } = useProject();
  const codebase = codebases?.find(cb => cb.id === selectedProjectId);

  const {
    data: outcome,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['outcome', selectedProjectId, outcomeId],
    queryFn: () => getOutcomeDetail(selectedProjectId ?? '', outcomeId ?? ''),
    enabled: !!selectedProjectId && !!outcomeId,
  });

  const { data: conversationData } = useQuery({
    queryKey: ['outcome-conversation', selectedProjectId, outcomeId],
    queryFn: () => getOutcomeConversation(selectedProjectId ?? '', outcomeId ?? ''),
    enabled: !!selectedProjectId && !!outcomeId,
  });

  const queryClient = useQueryClient();
  const currentPhase = outcome ? phaseIndex(outcome.status) : 0;

  // Convergence score editing
  const [editingScore, setEditingScore] = useState(false);
  const [manualPointsInput, setManualPointsInput] = useState('');
  const [manualAssessmentStatus, setManualAssessmentStatus] = useState<'completed' | 'reviewed'>(
    'completed'
  );
  const parsedManualPoints = Number(manualPointsInput);
  const manualPointsValid =
    manualPointsInput.trim().length > 0 &&
    Number.isInteger(parsedManualPoints) &&
    parsedManualPoints >= 0 &&
    parsedManualPoints <= 10;

  const scoreMutation = useMutation({
    mutationFn: ({ points, status }: { points: number; status: 'completed' | 'reviewed' }) =>
      updateOutcomeConvergenceScore(selectedProjectId ?? '', outcomeId ?? '', points, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['outcome', selectedProjectId, outcomeId] });
      setEditingScore(false);
    },
  });

  // Status transition
  const statusMutation = useMutation({
    mutationFn: (status: OutcomeSummary['status']) =>
      updateOutcomeStatus(selectedProjectId ?? '', outcomeId ?? '', status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['outcome', selectedProjectId, outcomeId] });
    },
  });

  // Execute outcome
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
  const executeMutation = useMutation({
    mutationFn: () =>
      executeOutcome(selectedProjectId ?? '', outcomeId ?? '', selectedWorkflow || undefined),
    onSuccess: data => {
      void queryClient.invalidateQueries({ queryKey: ['outcome', selectedProjectId, outcomeId] });
      void navigate(`/chat/${data.conversationId}`);
    },
  });

  const WORKFLOW_OPTIONS = [
    { value: '', label: 'Auto-select (recommended)' },
    { value: 'maestro-outcome-lifecycle', label: 'Full Lifecycle' },
    { value: 'maestro-piv-loop', label: 'PIV Loop (focused fix)' },
    { value: 'maestro-outcome-quick', label: 'Quick Cycle' },
  ] as const;

  function getExecuteButtonLabel(status: string): string {
    switch (status) {
      case 'approved':
        return 'Start Execution';
      case 'in-progress':
        return 'Resume Execution';
      case 'rework':
        return 'Re-execute (Rework)';
      case 'converging':
        return 'Run Convergence Check';
      default:
        return 'Execute Outcome';
    }
  }

  const canExecute = outcome ? !['draft', 'complete'].includes(outcome.status) : false;

  const STATUS_TRANSITIONS: Record<string, OutcomeSummary['status'][]> = {
    draft: ['approved'],
    approved: ['in-progress'],
    'in-progress': ['converging', 'rework'],
    converging: ['complete', 'conditional', 'rework'],
    conditional: ['in-progress', 'complete'],
    rework: ['in-progress'],
  };

  const nextStatuses = outcome ? (STATUS_TRANSITIONS[outcome.status] ?? []) : [];

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title={outcome ? outcome.title : 'Outcome Detail'} projectName={codebase?.name} />
      <PageBody maxWidthClassName="max-w-5xl" contentClassName="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void navigate('/outcomes');
          }}
          className="mb-2"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Outcomes
        </Button>

        {!selectedProjectId && (
          <div className="space-y-3">
            <label htmlFor="outcome-project-selector" className="text-sm text-text-secondary">
              Select a project to view this outcome:
            </label>
            <select
              id="outcome-project-selector"
              value=""
              onChange={(e): void => {
                setSelectedProjectId(e.target.value || null);
              }}
              className="h-9 rounded-md border border-border bg-surface-elevated text-text-primary px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring w-full max-w-md"
            >
              <option value="">Select a project...</option>
              {isLoadingCodebases ? (
                <option disabled>Loading...</option>
              ) : (
                codebases?.map((cb: CodebaseResponse) => (
                  <option key={cb.id} value={cb.id}>
                    {cb.name}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {isLoading && selectedProjectId && (
          <div className="text-sm text-muted-foreground">Loading outcome...</div>
        )}

        {error && (
          <div className="text-sm text-error">
            Failed to load outcome: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {outcome && (
          <>
            <section className="relative overflow-hidden rounded-[28px] border border-border/70 bg-[linear-gradient(135deg,rgba(59,130,246,0.12),rgba(15,23,42,0.02)_38%,rgba(16,185,129,0.08))] p-5 shadow-sm">
              <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/20 to-transparent opacity-60" />
              <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-text-tertiary">
                      Outcome Command Deck
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {/*
                        This badge is the page's durable status readout: it renders for every
                        status. The one in the "Status Transition" card below is not — that
                        whole card unmounts once no transitions remain (see nextStatuses), so
                        it disappears exactly when the status reaches a terminal value like
                        'complete'. Assert status against THIS one.
                      */}
                      <Badge
                        data-testid="outcome-status"
                        variant={outcome.status === 'complete' ? 'default' : 'secondary'}
                      >
                        {outcome.status}
                      </Badge>
                      <span className="font-mono text-xs text-text-tertiary">{outcome.id}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h1 className="max-w-3xl text-2xl font-semibold tracking-tight text-text-primary md:text-3xl">
                      {outcome.title}
                    </h1>
                    <p className="max-w-2xl text-sm leading-6 text-text-secondary">
                      Guide this outcome from definition into verified delivery, keep the evidence
                      trail tight, and use AI or workflow execution only where it moves the
                      convergence line forward.
                    </p>
                  </div>
                </div>
                <div className="grid min-w-[16rem] gap-3 sm:grid-cols-2 lg:grid-cols-1">
                  <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm backdrop-blur-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                      Convergence
                    </div>
                    <div
                      className={cn(
                        'mt-2 text-3xl font-semibold tracking-tight',
                        convergenceColor(outcome.convergenceScore)
                      )}
                    >
                      {outcome.convergenceScore !== null ? `${outcome.convergenceScore}%` : '--'}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3 shadow-sm backdrop-blur-sm">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                      Activity
                    </div>
                    <div className="mt-2 text-sm font-medium text-text-primary">
                      Updated {formatTimestamp(outcome.updatedAt)}
                    </div>
                    <div className="mt-1 text-xs text-text-secondary">
                      Created {formatTimestamp(outcome.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Phase progress stepper */}
            <Card className="border-border/70 bg-surface/90 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <span className="font-mono text-sm text-text-secondary">
                    {outcome.id.slice(0, 8)}
                  </span>
                  <Badge variant={outcome.status === 'complete' ? 'default' : 'secondary'}>
                    {outcome.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1">
                  {PHASES.map((phase, i) => (
                    <div key={phase} className="flex flex-1 items-center">
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={cn(
                            'flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium',
                            i < currentPhase
                              ? 'bg-success text-white'
                              : i === currentPhase
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-surface-elevated text-text-tertiary border border-border'
                          )}
                        >
                          {i + 1}
                        </div>
                        <span
                          className={cn(
                            'mt-1 text-xs',
                            i <= currentPhase ? 'text-text-primary' : 'text-text-tertiary'
                          )}
                        >
                          {phase}
                        </span>
                      </div>
                      {i < PHASES.length - 1 && (
                        <div
                          className={cn(
                            'h-0.5 flex-1 mx-1',
                            i < currentPhase ? 'bg-success' : 'bg-border'
                          )}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Dependencies */}
            {outcome.dependsOn && outcome.dependsOn.length > 0 && (
              <Card
                data-testid="outcome-dependencies"
                className="border-border/70 bg-surface/90 shadow-sm"
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                      Depends On
                    </span>
                    {outcome.dependsOn.map(depId => (
                      <button
                        key={depId}
                        type="button"
                        className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 font-mono text-xs text-primary transition-colors hover:bg-primary/20"
                        onClick={(): void => {
                          void navigate(`/outcomes/${depId}`);
                        }}
                      >
                        {depId}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Execute Outcome */}
            {canExecute && (
              <Card className="border-border/70 bg-surface/90 shadow-sm">
                <CardContent className="grid gap-4 py-5 lg:grid-cols-[1.2fr_auto] lg:items-end">
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-text-tertiary">
                        Execution Lane
                      </div>
                      <div className="text-sm text-text-secondary">
                        Choose a workflow strategy or let the platform pick the best path for the
                        current outcome state.
                      </div>
                    </div>
                    <label htmlFor="outcome-workflow-selector" className="sr-only">
                      Execution workflow
                    </label>
                    <select
                      id="outcome-workflow-selector"
                      value={selectedWorkflow}
                      onChange={(e): void => {
                        setSelectedWorkflow(e.target.value);
                      }}
                      className="h-10 w-full max-w-md rounded-xl border border-border bg-surface-elevated px-3 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      {WORKFLOW_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {executeMutation.isError && (
                      <span className="text-xs text-error">
                        {executeMutation.error instanceof Error
                          ? executeMutation.error.message
                          : 'Execution failed'}
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      executeMutation.mutate();
                    }}
                    disabled={executeMutation.isPending}
                    className="h-10 shrink-0 rounded-xl px-5"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    {executeMutation.isPending
                      ? 'Starting...'
                      : getExecuteButtonLabel(outcome?.status ?? '')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Convergence Score + Status Actions + AI Refinement */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card className="border-border/70 bg-surface/90 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Convergence Score
                    {!editingScore && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Edit manual convergence assessment"
                        disabled={!outcome.convergenceAssessment?.breakdown}
                        onClick={() => {
                          setManualPointsInput(
                            String(outcome.convergenceAssessment?.manualAssessment.points ?? 0)
                          );
                          setManualAssessmentStatus(
                            outcome.convergenceAssessment?.manualAssessment.status === 'reviewed'
                              ? 'reviewed'
                              : 'completed'
                          );
                          setEditingScore(true);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editingScore ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="manual-convergence-points"
                          className="text-xs text-text-secondary"
                        >
                          Manual points
                        </label>
                        <Input
                          id="manual-convergence-points"
                          aria-label="Manual convergence points"
                          type="number"
                          min={0}
                          max={10}
                          step={1}
                          aria-invalid={manualPointsInput.length > 0 && !manualPointsValid}
                          value={manualPointsInput}
                          onChange={e => {
                            setManualPointsInput(e.target.value);
                          }}
                          className="w-20"
                          placeholder="0-10"
                        />
                        <span className="text-sm">/10</span>
                      </div>
                      {manualPointsInput.length > 0 && !manualPointsValid && (
                        <div className="text-xs text-error" role="alert">
                          Manual points must be a whole number from 0 to 10.
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-xs text-text-secondary">
                        Review state
                        <select
                          aria-label="Manual convergence review state"
                          value={manualAssessmentStatus}
                          onChange={e => {
                            setManualAssessmentStatus(
                              e.target.value === 'reviewed' ? 'reviewed' : 'completed'
                            );
                          }}
                          className="h-8 rounded-md border border-border bg-surface-elevated px-2 text-text-primary"
                        >
                          <option value="completed">completed</option>
                          <option value="reviewed">reviewed</option>
                        </select>
                      </label>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Save manual convergence assessment"
                          onClick={() => {
                            if (manualPointsValid) {
                              scoreMutation.mutate({
                                points: parsedManualPoints,
                                status: manualAssessmentStatus,
                              });
                            }
                          }}
                          disabled={scoreMutation.isPending || !manualPointsValid}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Cancel manual convergence assessment"
                          onClick={() => {
                            setEditingScore(false);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className={`text-4xl font-bold ${convergenceColor(outcome.convergenceScore)}`}
                      >
                        {outcome.convergenceScore !== null ? `${outcome.convergenceScore}%` : '--'}
                      </div>
                      {outcome.convergenceScore === null && (
                        <div className="mt-1 text-xs text-text-tertiary">
                          Run automated convergence scoring to enable the manual assessment.
                        </div>
                      )}
                      {outcome.convergenceScore !== null && selectedProjectId && outcomeId && (
                        <div className="mt-3">
                          <ConvergenceChart codebaseId={selectedProjectId} outcomeId={outcomeId} />
                        </div>
                      )}
                    </>
                  )}
                  {scoreMutation.isError && (
                    <div className="mt-2 text-xs text-error" role="alert">
                      {scoreMutation.error instanceof Error
                        ? scoreMutation.error.message
                        : 'Manual assessment could not be saved'}
                    </div>
                  )}
                </CardContent>
              </Card>

              <ConvergenceBreakdownCard
                assessment={outcome.convergenceAssessment}
                availability={outcome.convergenceAssessmentAvailability}
                error={outcome.convergenceAssessmentError}
              />

              {nextStatuses.length > 0 && (
                <Card className="border-border/70 bg-surface/90 shadow-sm">
                  <CardHeader>
                    <CardTitle>Status Transition</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-text-secondary mb-3">
                      Current: <Badge variant="secondary">{outcome.status}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {nextStatuses.map(status => (
                        <Button
                          key={status}
                          size="sm"
                          variant={status === 'complete' ? 'default' : 'outline'}
                          onClick={() => {
                            statusMutation.mutate(status);
                          }}
                          disabled={statusMutation.isPending}
                        >
                          {status}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI Refinement Chat */}
              <OutcomeChatPanel
                codebaseId={selectedProjectId ?? ''}
                outcomeId={outcomeId ?? ''}
                outcomeTitle={outcome.title}
                existingConversationId={conversationData?.conversationId ?? null}
              />

              {/* Test Coverage */}
              <TestLayerCard
                codebaseId={selectedProjectId ?? ''}
                outcomeId={outcomeId ?? ''}
                hasTestPlan={'test-outcome-plan.md' in outcome.artifacts}
              />
            </div>

            {/* Evidence / Artifacts */}
            {Object.keys(outcome.artifacts).length > 0 && (
              <Card className="border-border/70 bg-surface/90 shadow-sm">
                <CardHeader>
                  <CardTitle>Evidence &amp; Artifacts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {Object.entries(outcome.artifacts).map(([key, value]) => (
                      <div
                        key={key}
                        className="rounded-2xl border border-border/70 bg-background/80 p-3 shadow-sm"
                      >
                        <div className="text-xs font-medium text-text-secondary mb-1">{key}</div>
                        <pre className="text-xs text-text-primary whitespace-pre-wrap break-words max-h-48 overflow-auto">
                          {value}
                        </pre>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Audit Trail */}
            <Card className="border-border/70 bg-surface/90 shadow-sm">
              <CardHeader>
                <CardTitle>Audit Trail</CardTitle>
              </CardHeader>
              <CardContent>
                {outcome.auditTrail.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No audit entries yet.</div>
                ) : (
                  <div className="relative border-l border-border pl-4 space-y-4">
                    {outcome.auditTrail.map((entry: AuditEntry, i: number) => (
                      <div key={i} className="relative">
                        <div className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
                        <div className="text-xs text-text-tertiary">
                          {formatTimestamp(entry.timestamp)}
                        </div>
                        <div className="text-sm text-text-primary">
                          <span className="font-medium">{entry.action}</span>
                          <span className="text-text-secondary ml-2">by {entry.actor}</span>
                        </div>
                        {entry.details && (
                          <div className="mt-0.5 text-xs text-text-secondary">{entry.details}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </PageBody>
    </div>
  );
}
